import { useRef, useState } from "react";
import type { Vote } from "./forum-room.types";

export function useForumVotes({
  submit,
}: {
  readonly submit: (vote: {
    readonly id: string;
    readonly value: -1 | 0 | 1;
  }) => Promise<unknown>;
}) {
  const [votes, setVotes] = useState<Record<string, Vote>>({});
  const pending = useRef(new Set<string>());

  function vote({
    id,
    direction,
  }: {
    readonly id: string;
    readonly direction: 1 | -1;
  }) {
    if (pending.current.has(id)) return;

    pending.current.add(id);
    const previous = votes[id]?.value ?? 0;
    const next = previous === direction ? 0 : direction;
    setVotes((current) => ({
      ...current,
      [id]: { value: next, busy: true, delta: next - previous },
    }));
    submit({ id, value: next })
      .then(() =>
        setVotes((current) => ({
          ...current,
          [id]: { value: next, busy: false },
        })),
      )
      .catch(() =>
        setVotes((current) => ({
          ...current,
          [id]: {
            value: previous,
            busy: false,
            error: "Couldn't record your vote. Please try again.",
          },
        })),
      )
      .finally(() => pending.current.delete(id));
  }

  return { votes, vote };
}
